// Slice H2 Week 2 — canonical risk_rule body strings for curated templates.
//
// Why this file exists.
//   The H2 v1 templates referenced risk_rule blocks but used the WRONG
//   canonical shape (Dogfood Issue #2): missing __canonical marker,
//   missing executable `code` body, wrong parameter field names. As a
//   result, the engine treated them as non-canonical user-authored
//   risk_rule blocks with no code → silent no-ops; documented per-trade
//   risk caps were not actually enforced.
//
//   The Week 2 fix embeds the EXACT canonical bodies the engine
//   dispatches at the resolveQty / checkEntry / onTradeClosed /
//   onDayRollover sites. Body strings are pasted here (not imported
//   from engine/riskRules.ts) for two reasons:
//
//     1. Templates are static TS data shipped with the app. Importing
//        runtime engine modules would couple them to the engine's
//        broader dependency graph (worker imports etc.) — same reason
//        G7 extracted hashParams into its own leaf module.
//     2. The bodies are stable engine contracts. They live alongside
//        the test fixtures that already pin them (riskRuleP1 group
//        in scripts/test-conversion.mjs). Inlining keeps the slice
//        self-contained.
//
// Source of truth.
//   Each body string is byte-identical to the body verified in the
//   conversion-test fixture at scripts/test-conversion.mjs:9603+
//   (P1-track tests). If those tests pass, these bodies are correct.
//
// Safety contract.
//   This file ONLY exports body strings + canonical marker constants.
//   It does NOT execute or interpret the bodies — that's the engine's
//   job. The templates that consume these constants are pure data;
//   the engine compiles + dispatches them at backtest time.

/** Canonical marker for the max_risk_per_trade_pct_v1 rule. Used in
 *  parameters.__canonical. Bit-identical to
 *  `engine/riskRules.ts:CANONICAL_MAX_RISK_PER_TRADE`. */
export const CANONICAL_MAX_RISK_PER_TRADE = 'max_risk_per_trade_pct_v1';

// TSL migration: the executable `resolveQty` body for max_risk_per_trade_pct_v1 has been REMOVED. The rule
// is enforced entirely by the engine's trusted isCanonicalMaxRiskPerTrade path (riskRules.ts), dispatched
// by the marker above — the JS body was vestigial. Templates carry the marker + params only, no code.

/** Default parameter shape for a max_risk_per_trade_pct_v1 risk_rule block.
 *  Caller-extendable — pass an overrides object to set maxRiskPerTradePct
 *  or change mode. */
export function maxRiskPerTradeParameters(maxRiskPerTradePct = 1.0, mode: 'resize' | 'reject' = 'resize') {
  return {
    maxRiskPerTradePct,
    mode,
    enabled: true,
    __canonical: CANONICAL_MAX_RISK_PER_TRADE,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Iter 4B/7 — run-up-then-reverse stateful exit (price AND/OR indicator reversal).
//
// A position-management risk_rule using the checkBar position+bar ctx (Iter 4B:
// positionSide / positionEntryPrice / positionEntryBarIndex / barHigh/Low/Close)
// AND the connected-input snapshot (Iter 7: ctx.inputSignals[i] from the rule's
// serializable connections.inputs). Semantics:
//   • track max favorable excursion (peak) bar-by-bar in `state` (causal — only
//     current+past bars; never the throttled engine peakHigh/peakLow, never future);
//   • ARM once run-up from entry ≥ runUpPct;
//   • FLATTEN once the configured reversal fires:
//       reverseMode='price'     → price gives back ≥ giveBackPct from the peak;
//       reverseMode='indicator' → the side's connected reversal signal is true
//                                  (params.longReverseInput / shortReverseInput index
//                                  into ctx.inputSignals — e.g. a TSI/TEMA flip);
//       reverseMode='both'      → price AND indicator;
//       reverseMode unset       → indicator if inputs are wired, else price;
//   • separate long/short; reset on flat (side === null) and on a new entry-bar.
// CAUSAL: indicator signals are bar-i (closed-bar) values — no lookahead. The rule
// reads ONLY its explicit connections.inputs (via ctx.inputSignals), never arbitrary
// global graph state. Runs additively. Serializable (a risk_rule CUSTOM block + inputs).
//
// TSL migration: the executable checkBar body has been REMOVED and promoted to the canonical
// `run_up_reverse_v1` rule — trusted TS in riskRules.ts (CANONICAL_CHECKBAR_FNS), dispatched by the
// __canonical marker the Pine importer now stamps. The block carries params + connections only, no code.

/** Default parameter shape for a price-giveback run-up-then-reverse risk_rule. */
export function runUpReverseParameters(runUpPct = 0.2, giveBackPct = 0.2) {
  return { runUpPct, giveBackPct, reverseMode: 'price', enabled: true };
}

/** Parameter shape for an INDICATOR-reversal run-up-then-reverse risk_rule. The reversal signals come
 *  from the block's connections.inputs (longReverseInput / shortReverseInput index into them). */
export function runUpReverseIndicatorParameters(runUpPct = 0.2, longReverseInput = 0, shortReverseInput = 1, giveBackPct?: number) {
  return { runUpPct, longReverseInput, shortReverseInput, reverseMode: giveBackPct ? 'both' : 'indicator', ...(giveBackPct ? { giveBackPct } : {}), enabled: true };
}
